import Dialog from '@anm/components/modals/Dialog';
import ConfirmDelete from '@anm/components/notification/ConfirmDelete';
import noop from '@anm/helpers/noop';
import isDestinationDisconnected from '@anm/helpers/stream/isDestinationDisconnected';
import useHover from '@anm/hooks/useHover';
import useSwitchState from '@anm/hooks/useSwitchState';
import { useCallback, useEffect, FC } from 'react';

import { DeleteImage, DeleteImageContainer, ItemWrapper, ListWrapper } from './Wrapper';
import ConnectButton from './components/ConnectButton';
import DestinationInfo from './components/DestinationInfo';
import { DestinationListProps, DestinationRowProps } from './types';

export * from './types';
export { ItemWrapper, DestinationInfo };

const DestinationRow: FC<DestinationRowProps> = ({ onDelete, isPending, onReconnect, isDeleteError, ...infoProps }) => {
  const [rowRef, isRowHovered] = useHover<HTMLDivElement>();
  const [isConfirm, openConfirm, closeConfirm] = useSwitchState();

  const { id, title, kind, network } = infoProps;

  const isDisconnected = isDestinationDisconnected(infoProps);

  useEffect(() => {
    const canCloseModal = isDeleteError && isConfirm;

    canCloseModal && closeConfirm();
  }, [isDeleteError, isConfirm]);

  const handleDelete = useCallback(() => {
    onDelete(id);
  }, [id, onDelete]);

  const deleteEndDescription = network !== 'FACEBOOK' ? 'channel' : kind;

  return (
    <>
      {isConfirm && (
        <Dialog onModalClose={closeConfirm} onSubmit={handleDelete}>
          <ConfirmDelete
            title="Remove destination"
            description={`Are you sure to remove <b>${title} ${deleteEndDescription}?</b>`}
            onClose={closeConfirm}
            isPending={isPending}
            onDelete={handleDelete}
          />
        </Dialog>
      )}
      <ItemWrapper ref={rowRef}>
        <DestinationInfo {...infoProps} isDisconnected={isDisconnected} />
        {isDisconnected && <ConnectButton {...{ onReconnect }} />}
        {isRowHovered && (
          <DeleteImageContainer onClick={openConfirm}>
            <DeleteImage alt="delete destination" src="images/icons/trash.svg" />
          </DeleteImageContainer>
        )}
      </ItemWrapper>
    </>
  );
};

const DestinationList: FC<DestinationListProps> = ({ list, destinations, ...rest }) => (
  <ListWrapper>
    {list.map(item => {
      const currentDestination = destinations?.find(
        destination => destination.network === item.network && destination.kind === item.kind
      );

      const handler = currentDestination?.onClick || noop;
      const handleClick = () => handler(currentDestination?.title);

      return <DestinationRow key={item.id} onReconnect={handleClick} {...{ ...item, ...rest }} />;
    })}
  </ListWrapper>
);

export default DestinationList;
